import { ApplicationResolution } from '../applicationResolution';
import { getAssetUrl } from '../utils/cloudinary';
import { Control, Image, Language, View } from '@yleisradio/areena-types';
import { IconType, UiDetails, UiPlayControl } from './typings';
import { isActivator } from '../typeGuards';
import { getRawLabel, labelsOf } from '../utils/labels';
import { GeoLocation } from '../appTypings';
import { LoggedInUser, Mode } from '../../common/types';
import { getIsGeoBlocked, getRegion } from '../utils/geoBlock';
import { isServicePointerAndHbbtv } from '../utils/contentBlock';
import { getButtonText } from '../utils/buttonText';

export function getUiDetailsFromItem(itemView: View): UiDetails {
  return {
    image: itemView.header?.image || itemView.image,
    logoImage: itemView.header?.overlayLogoImage,
    seriesTitle: itemView.header?.title ? itemView.title : undefined,
    description: itemView.header?.description,
    keywordLabels: labelsOf('generic', itemView.header?.labels),
    title: itemView.header?.title || itemView.title,
    isAudio:
      itemView.header?.labels?.find(label => label.type === 'mediaType')
        ?.raw === 'audio',
  };
}

export function getHeaderItemIdLabel(ItemView: View): string | undefined {
  const itemIdLabel = ItemView.header?.labels?.find(
    label => label.type === 'itemId'
  );
  return itemIdLabel?.raw;
}

const getContentRatingImageUrl = (
  label: any,
  lang: string,
  applicationResolution: ApplicationResolution
) => {
  const options = {
    format: 'png',
    width: applicationResolution === ApplicationResolution.FULLHD ? 67 : 45,
  };

  if (label.type === 'contentRating.ageRestriction' && label.raw) {
    const ageRestriction = label.raw;
    const img = {
      id: 'ageRestriction-' + ageRestriction + '_' + lang,
      version: '',
    };

    return getAssetUrl(img, options);
  }
  if (label.type === 'contentRating.reason' && label.raw) {
    const reason = label.raw;
    const img = { id: reason, version: '' } as Image;

    return getAssetUrl(img, options);
  }
};

export function getUiPlayControl(
  itemView: View,
  geoLocation: GeoLocation,
  language: Language,
  applicationResolution: ApplicationResolution,
  mode: Mode
): UiPlayControl | undefined {
  const { cards: [playCard] = [] } = itemView.header || {};
  if (!playCard) return undefined;

  const isGeoBlocked = getIsGeoBlocked(
    geoLocation,
    getRegion(playCard.labels || [])
  );

  const restrictionLabels = labelsOf(
    ['contentRating.ageRestriction', 'contentRating.reason'],
    playCard.labels,
    true
  );
  const destination = playCard.controls?.[0]?.destination;

  const isBlocked = isServicePointerAndHbbtv(destination || { uri: '' }, mode);
  return {
    buttonText: getButtonText(
      isGeoBlocked,
      playCard?.controls?.[0]?.title,
      language,
      getRawLabel(playCard, 'itemtype')
    ),
    contentRatingImages: restrictionLabels.map(
      label =>
        getContentRatingImageUrl(label, language, applicationResolution) || ''
    ),
    contentRatingFormattedLabels: restrictionLabels.map(
      label => label.formatted || ''
    ),
    cssClasses: '',
    genericLabels: labelsOf('generic', playCard.labels),
    isBlocked,
    isGeoBlocked,
    pointer: destination,
    title: playCard.title || '',
  };
}

export const getIconType = (id: string | undefined): IconType | undefined => {
  if (id === 'icon-view-module') {
    return 'recommendations';
  }
  if (id === 'icon-video-library') {
    return 'episodes';
  }
  if (id === 'icon-favorite-off') {
    return 'heart';
  }
  if (id === 'icon-favorite-on') {
    return 'favorite';
  }
};

const filterSubAccountRestrictedControls = (
  user: LoggedInUser | null,
  control: Control
) => {
  const isSubAccountRestricted =
    user?.isSubAccount && control?.audience?.subAccount === false;
  return !isSubAccountRestricted;
};

export function filterControls(
  controls: Control[] | undefined,
  user: LoggedInUser | null
) {
  return controls
    ?.filter(control => control && !isActivator(control))
    .filter(control => filterSubAccountRestrictedControls(user, control));
}

export const getItemId = (id: string): string | undefined =>
  /^1-\d+$/.test(id) ? id : undefined;
